/**
 * Asesora de plataforma — Owner SaaS advisor (FASE 17).
 * Not a KPI-only dashboard: Product Advisor, RC, Salud del producto, Debt, Release Notes.
 */
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  Loader2, RefreshCw, Sparkles, ShieldCheck, HeartPulse,
  DollarSign, AlertTriangle, FileText, Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { clientRequestHeaders, ensureCsrfToken } from "@/lib/sm-api";
import { EXECUTIVE_LEXICON } from "@/lib/renata/executive/experience";
import { InlineExecutiveAi } from "@/components/renata/InlineExecutiveAi";

export const Route = createFileRoute("/admin/ops/ai/executive")({
  component: ExecutiveAiPage,
  validateSearch: (s: Record<string, unknown>) => ({
    ask: typeof s.ask === "string" ? s.ask : undefined,
  }),
  head: () => ({ meta: [{ title: "Asesora de plataforma — Admin" }] }),
});

type Snapshot = {
  health: { status: string; narrative: string };
  rc: { label: string; score: number; verdict: string; checks: Array<{ id: string; label: string; status: string; detail: string }>; observations: string[] };
  modules: Array<{ module: string; total: number }>;
  debt: { summary: string; findings: Array<{ kind: string; path: string; detail: string; priority: string; proposedAction: string }> };
  costs: { dailyUsd: string; monthlyUsd: string; projectedUsd: string } | null;
  tenants: { active: number; inactive: number; topAi: Array<{ tenantId: string; name?: string }>; topRenata: Array<{ tenantId: string }> };
  featuresCount: number;
  business: Record<string, number | null> | null;
  support: Record<string, number | null> | null;
};

function ExecutiveAiPage() {
  const search = useSearch({ from: "/admin/ops/ai/executive" });
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [ask, setAsk] = useState(search.ask ?? "");
  const [answer, setAnswer] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/ai/executive", { credentials: "include" });
      if (!res.ok) {
        setError(`Error ${res.status}`);
        setSnap(null);
        return;
      }
      setSnap((await res.json()) as Snapshot);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error de red");
    } finally {
      setLoading(false);
    }
  }

  async function submitAsk(message?: string) {
    const msg = (message ?? ask).trim();
    if (!msg) return;
    setAsking(true);
    setError(null);
    try {
      await ensureCsrfToken();
      const res = await fetch("/api/admin/ai/executive", {
        method: "POST",
        credentials: "include",
        headers: { ...clientRequestHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ action: "ask", message: msg }),
      });
      const data = (await res.json()) as { content?: string; error?: string };
      if (!res.ok) {
        setError(data.error ?? `Error ${res.status}`);
        return;
      }
      setAnswer(data.content ?? "");
      setAsk(msg);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error de red");
    } finally {
      setAsking(false);
    }
  }

  useEffect(() => { void load(); }, []);
  useEffect(() => {
    if (search.ask) void submitAsk(search.ask);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.ask]);

  const quick = [
    "¿Qué features casi nadie usa?",
    "¿Está listo para RC?",
    "Radar de deuda técnica",
    "¿Cuánto cuesta la IA este mes?",
    "Product health por módulo",
    "Generá release notes RC",
  ];

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-600" />
            {EXECUTIVE_LEXICON.home}
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Asesora de producto y operación para el Owner SaaS. Datos reales, sin inventar métricas ni mezclar tenants.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Actualizar
        </Button>
      </div>

      <InlineExecutiveAi />

      {error && (
        <div className="rounded-lg border border-red-300/60 bg-red-50/50 px-3 py-2 text-sm text-red-800 dark:bg-red-950/30 dark:text-red-200">
          {error}
        </div>
      )}

      {loading && !snap ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando evidencia de plataforma…
        </div>
      ) : snap ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat icon={HeartPulse} label={EXECUTIVE_LEXICON.health} value={snap.health.status} hint={snap.health.narrative} />
            <Stat icon={ShieldCheck} label={EXECUTIVE_LEXICON.rc} value={snap.rc.label} hint={`Puntaje ${snap.rc.score}/100`} />
            <Stat
              icon={DollarSign}
              label={EXECUTIVE_LEXICON.cost}
              value={snap.costs?.monthlyUsd ?? "sin dato"}
              hint={snap.costs ? `Día ${snap.costs.dailyUsd} · proy. ${snap.costs.projectedUsd}` : "Sin audit de costos"}
            />
            <Stat icon={AlertTriangle} label={EXECUTIVE_LEXICON.debt} value={`${snap.debt.findings.length} hallazgos`} hint={snap.debt.summary} />
          </div>

          <section className="grid gap-4 lg:grid-cols-2">
            <Panel title="Listo para publicar">
              <ul className="space-y-1.5 text-sm">
                {snap.rc.checks.map((c) => (
                  <li key={c.id} className="flex justify-between gap-2 border-b border-border/40 py-1 last:border-0">
                    <span>{c.label}</span>
                    <span className="tabular-nums text-muted-foreground">{c.status} · {c.detail}</span>
                  </li>
                ))}
              </ul>
              {snap.rc.observations[0] && (
                <p className="mt-2 text-xs text-muted-foreground">{snap.rc.observations[0]}</p>
              )}
            </Panel>
            <Panel title="Salud del producto (peor primero)">
              <ul className="space-y-1.5 text-sm">
                {snap.modules.slice(0, 8).map((m) => (
                  <li key={m.module} className="flex justify-between gap-2 border-b border-border/40 py-1 last:border-0">
                    <span>{m.module}</span>
                    <span className="tabular-nums font-medium">{m.total}</span>
                  </li>
                ))}
              </ul>
            </Panel>
          </section>

          <Panel title="Radar de deuda técnica (solo detección)">
            <ul className="space-y-2 text-sm">
              {snap.debt.findings.slice(0, 12).map((f, i) => (
                <li key={`${f.path}-${i}`} className="border-b border-border/40 pb-2 last:border-0">
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs font-medium uppercase tracking-wide text-amber-700 dark:text-amber-300">{f.priority}</span>
                    <span className="font-medium">{f.kind}</span>
                    <span className="text-muted-foreground truncate">{f.path}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{f.detail} → {f.proposedAction}</p>
                </li>
              ))}
              {!snap.debt.findings.length && (
                <li className="text-xs text-muted-foreground">Sin hallazgos con la evidencia actual.</li>
              )}
            </ul>
          </Panel>
        </>
      ) : null}

      <Panel title={EXECUTIVE_LEXICON.ask}>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {quick.map((q) => (
            <Button key={q} size="sm" variant="outline" className="h-7 text-xs" onClick={() => void submitAsk(q)} disabled={asking}>
              {q}
            </Button>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            className="flex-1 rounded-md border bg-background px-3 py-2 text-sm"
            value={ask}
            onChange={(e) => setAsk(e.target.value)}
            placeholder="Ej: ¿Qué módulos deberían simplificarse?"
            onKeyDown={(e) => { if (e.key === "Enter") void submitAsk(); }}
          />
          <Button onClick={() => void submitAsk()} disabled={asking || !ask.trim()}>
            {asking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
        {answer && (
          <pre className="mt-4 max-h-[420px] overflow-auto whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-xs leading-relaxed">
            {answer}
          </pre>
        )}
        <div className="mt-3 flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={async () => {
              await ensureCsrfToken();
              const res = await fetch("/api/admin/ai/executive", {
                method: "POST",
                credentials: "include",
                headers: { ...clientRequestHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify({ action: "release-notes", audience: "rc" }),
              });
              const data = await res.json() as { markdown?: string; warnings?: string[] };
              setAnswer(
                (data.markdown ?? "") +
                  (data.warnings?.length
                    ? ("\n\nAvisos:\n- " + data.warnings.join("\n- "))
                    : ""),
              );
            }}
          >
            <FileText className="h-3.5 w-3.5 mr-1" /> Notas de versión (candidato)
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={async () => {
              await ensureCsrfToken();
              const res = await fetch("/api/admin/ai/executive", {
                method: "POST",
                credentials: "include",
                headers: { ...clientRequestHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify({ action: "report", period: "weekly" }),
              });
              const data = await res.json() as { html?: string };
              setAnswer(data.html ?? "Sin HTML");
            }}
          >
            Resumen semanal (HTML)
          </Button>
        </div>
      </Panel>
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof HeartPulse;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-xl border p-4 space-y-1">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="text-lg font-semibold leading-tight">{value}</div>
      {hint && <p className="text-xs text-muted-foreground line-clamp-2">{hint}</p>}
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border p-4 space-y-3">
      <h2 className="text-sm font-semibold">{title}</h2>
      {children}
    </section>
  );
}
